import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Scrollbar } from 'react-scrollbars-custom';
import useBodyClass from '../../../common/hooks/useBodyClassName';
import { useAccessToken } from '../../../common/hooks/useAccessToken';
import { sortApartmentNumbers, isDemoPartnerAccount } from '../../../common/shared/statelessMethods';
import Button from '../../../common/Button';
import { CurrencyValue } from '../../../common/currencyValue';
import { constants } from '../../../common/constants/constants';
import PrintPdfDropdown from '../../../common/export_documents/PrintPdfDropdown';
import { parseApartmentPhone, parseApartmentTenantName } from '../../../store/common/tenantInfo';
import Filter from '../../../images/newDesign/filters.nocolor.svg';
import { BorderedPageWrapperStyled } from '../tenantList/TenantList.styled';
import SendToAllModal from '../debt_list/SendToAllModal';
import DebtCard from './DebtCard';
import AptInfo from './AptInfo';
import { StyledPdfWrapper } from './PdfWrapper.styled';
import PdfRow from './DebtPdfRow';
import TenantRow from './TenantPdfRow';
import { hideLoader, showLoader } from "../../../store/common/slice";
import { RoundButton } from "../../../ui/RoundButton";
import { FilteringModal } from "./FilteringModal";
import { PageTitle } from '../../../common/PageTitle';
import { selectCurrentBuildingCurrency } from "../../../store/buildings/selectors";
import ErrorsHandler from "../../../common/errors/handleErrors";
import { parseValues } from "../../../common/utils/yearlyRowsLogic";
import BllinkUtils from "../../../common/utils/utils";

const EXPORT_XLS_SETTINGS = {
    columnTypes: [
        {index: 3, type: 'Number'},
        {index: 4, type: 'Number'},
        {index: 6, type: 'Number'},
        {index: 8, type: 'Number'}
    ]
};

const DebtListNew = ({ match, address }) => {
    const { t } = useTranslation();
    const ltr = useSelector(state => state.common.ltrEnabled);
    const titleData = useSelector(state => state.buildings.titleData);
    const currency = useSelector(selectCurrentBuildingCurrency);
    const buildingTitleText = useSelector(state => state.buildings.titleText);
    const { buildingID, partnerID } = match.params;
    const client = useAccessToken();
    const dispatch = useDispatch();

    const [debtsLoading, setDebtsLoading] = useState(false);
    const [totalDebt, setTotalDebt] = useState();
    const [apartments, setApartments] = useState([]);
    const [selectedApt, setSelectedApt] = useState(null);

    const [showSendToAllModal, setShowSendToAllModal] = useState(false);
    const [showFilteringModal, setShowFilteringModal] = useState(false);
    const [title, setTitle] = useState('');
    const [partnerStats, setPartnerStats] = useState(null);
    const [parsedRecords, setParsedRecords] = useState([]);
    const [filters, setFilters] = useState({ thisMonthDebts: false });

    const titleRow = useMemo(() => ([{
        apartmentNumber: t('reports.apartmentName'),
        tenantName: t('editBuilding.labels.tenant'),
        phone: t('reports.tenantPhone'),
        totalDebt: t('reports.total_debt'),
        monthlyDebt: t('reports.monthly_debt'),
        monthlyDetails: t('reports.details'),
        specialDebt: t('reports.special_debt'),
        specialDetails: t('reports.details'),
        monthlyFee: t('reports.monthlyFee')
    }]), [t]);

    const pdfContainer = useRef();
    const [pdfContainerActive, setPdfContainerActive] = useState(false);
    const pdfContainerActivate = () => setPdfContainerActive(true);
    const csvCallback = item => setParsedRecords(prev => prev.concat([item]));

    useBodyClass('report');

    async function getPartnerData(client) {
        if (isDemoPartnerAccount(partnerID)) {
            const fileName = constants.getDemoPartnerProfileDataFileName(partnerID);
            const partnerProfileData = await import(`../../../common/mocks/data/managers/${fileName}`);
            return partnerProfileData.value;
        }
        try {
            const data = await client.partners.dashboard.getStats(partnerID);
            return data;
        } catch (e) {
            ErrorsHandler.notifyClientError(e);
            return { partnerStats: {} };
        }
    }

    async function loadDebtList(client, partnerID, buildingID) {

        let response
        if (isDemoPartnerAccount(partnerID)) {
            const fileName = constants.getDemoPartnerDebtFileName(partnerID);
            const partnerDebt = await import (`../../../common/mocks/data/managers/${fileName}`);
            response = partnerDebt.value;
        } else if (buildingID == null) {
            response = await client.managers.debts.getPartnerDebts(partnerID) ?? { value: [] };
        } else {
            // using large range since this new debt report does NOT have pagination yet
            response = await client.managers.debts.getBuildingDebts(buildingID, 0, 300) ?? { value: [] };
        }

        response.value = response?.value?.sort((a, b) => sortApartmentNumbers(a.apartmentNumber, b.apartmentNumber)) ?? [];

        for (const item of response.value) {
            item.tenantPhone = parseApartmentPhone(item.tenantDetails);
            item.tenantName = parseApartmentTenantName(item.tenantDetails);
        }

        return response;
    }

    function handleFiltersChange(f) {
        setFilters(f);
        setShowFilteringModal(false);
    }

    useEffect(async () => {
        if (!client) return;
        if (buildingID) {
            setTitle(buildingTitleText);
        } else {
            try {
                dispatch(showLoader());
                const partnerData = await getPartnerData(client);
                setPartnerStats(partnerData?.partnerStats);
                setTitle(partnerData?.partnerStats?.partner?.partnerName);
            } catch (e) {
                console.error(e);
            } finally {
                dispatch(hideLoader());
            }
        }
    }, [client, buildingTitleText]);

    useEffect(async () => {
        if (!client) return;
        if (debtsLoading) return;
        try {
            dispatch(showLoader());
            setDebtsLoading(true);
            const response = await loadDebtList(client, partnerID, buildingID);
            setTotalDebt(response.totalDebt?.value ?? '');
            setApartments(response.value);
        } catch (e) {
            console.error(e);
        } finally {
            setDebtsLoading(false);
            dispatch(hideLoader());
        }
    }, [client]);

    const filteredApartments = useMemo(() => {
        let filtered = [...(apartments ?? [])];
        if (filters?.thisMonthDebts) {
            const currentDate = new Date();
            const currentMonth = currentDate.getMonth() + 1;
            const currentYear = currentDate.getFullYear();
            const isThisMonthDebtsOnly = a => a.openMonthDebtPayments.length === 1 &&
                a.openMonthDebtPayments[0].month == currentMonth &&
                a.openMonthDebtPayments[0].year == currentYear;
            filtered = filtered.filter(a => !isThisMonthDebtsOnly(a));
        }
        return filtered;
    }, [apartments, filters]);

    const apartmentTenantList = useMemo(() => {
        return filteredApartments.map(item => ({
            label: item.apartmentNumber,
            value: {
                buildingID: item.buildingID,
                owner: item.tenantDetails.OwnerEmail,
                renter: item.tenantDetails.RenterEmail,
                openMonthDebtPayments: item.openMonthDebtPayments,
            }
        }));
    }, [filteredApartments]);

    const totalApts = useMemo(() => filteredApartments.length, [filteredApartments]);

    useEffect(() => {
        if (selectedApt != null) {
            const apt = filteredApartments.find(a => a.apartmentNumber === selectedApt.apartmentNumber);
            if (apt == null) {
                // previously selected apartment gone after filtering applied -> select first from the list
                setSelectedApt(filteredApartments[0]);
            }
        } else {
            setSelectedApt(filteredApartments[0]);
        }
    }, [filteredApartments]);

    const getExcelData = useCallback((type) => {
        const formatNumbers = type === 'pdf';
        if (!parsedRecords?.length) {
            return [];
        }
        return parsedRecords.map((record) => {
            const resultRecord = { ...record };
            resultRecord.monthlyDebt = formatNumbers ? parseValues(resultRecord.monthlyDebt) : resultRecord.monthlyDebt;
            resultRecord.specialDebt = formatNumbers ? parseValues(resultRecord.specialDebt) : resultRecord.specialDebt;
            resultRecord.monthlyFee = formatNumbers
                ? parseValues(resultRecord.monthlyFee)
                : Number(BllinkUtils.Strings.parseNumericStringWithCommasToNumber(resultRecord.monthlyFee));
            resultRecord.totalDebt = formatNumbers
                ? parseValues(resultRecord.totalDebt)
                : Number(BllinkUtils.Strings.parseNumericStringWithCommasToNumber(resultRecord.totalDebt));
            return resultRecord;
        });
    }, [parsedRecords])

    const renderCards = (apt, i, pdf) => {
        return (
            <DebtCard
                key={i}
                apt={apt}
                selectedApt={!pdf ? selectedApt : null}
                num={i}
                onClick={num => setSelectedApt(filteredApartments[num])}
                buildingID={buildingID}
                csvCallback={csvCallback}
                currency={currency}
            />
        )
    };

    const renderPdfRows = (apt, i) => <PdfRow apt={apt} key={i}/>;

    const renderTenantRows = (apt, i) => <TenantRow apt={apt} key={i}/>;

    return (
        <BorderedPageWrapperStyled ltr={ltr}>
            <PageTitle text={t('reports.debt_report_title')} subtitle={t('reports.debt_report_subtitle')}>
                <PrintPdfDropdown
                    buildingID={buildingID}
                    eventLabel={buildingID ? 'buildingDebtList' : 'partnerDebtList'}
                    titleString={buildingID ? t('reports.building_debt_list') : t('reports.partner_debt_list')}
                    excelTitle={buildingID ? t('reports.building_debt_list') : t('reports.partner_debt_list')}
                    reportName={t('reports.debt_report_title')}
                    pdfContainer={pdfContainer.current}
                    onClick={pdfContainerActivate}
                    rows={getExcelData}
                    titleRow={titleRow}
                    address={address}
                    disablePdf
                    xlsSettings={EXPORT_XLS_SETTINGS}
                />
            </PageTitle>

            <div className='main-wrapper ensure-height'>
                <div className='first-container'>
                    <Scrollbar
                        style={{ width: '100%', height: 'calc(100vh - 100px)' }}
                        trackYProps={{
                            renderer: props => {
                                const { elementRef, ...restProps } = props;
                                return <div {...restProps} ref={elementRef} className="scroll-track"/>;
                            }
                        }}
                        thumbYProps={{
                            renderer: props => {
                                const { elementRef, ...restProps } = props;
                                return <div {...restProps} ref={elementRef} className="scroll-thumb"/>;
                            }
                        }}
                        rtl={false}
                        noScrollX
                    >
                        <>
                            <div className='top-bar-large'>
                                <div className='top-bar-inner'>
                                    <RoundButton
                                        icon={Filter}
                                        title={t('reports.filtering')}
                                        onClick={() => setShowFilteringModal(true)}
                                    />
                                    <div className='total-apartments'>
                                        {t('reports.total_apartments')}: <span>{totalApts}</span>
                                    </div>
                                </div>

                                <div className='top-bar-inner second'>
                                    <Button rounded={false} onClick={() => setShowSendToAllModal(true)}>
                                        {t('reports.paymentRequestPopUp.sendAll')}
                                    </Button>
                                    <div className='total-apartments'>
                                        {t('reports.total_debts')}: <CurrencyValue notInCents
                                                                                   currency={currency}>{totalDebt}</CurrencyValue>
                                    </div>
                                </div>
                            </div>
                            <div className='cards-wrapper'>
                                {filteredApartments.length > 0 && filteredApartments.map((apt, i) => renderCards(apt, i))}
                                {filteredApartments.length === 0 && (
                                    <div className="empty-table">{t('reports.empty_table')}</div>
                                )}
                            </div>
                        </>
                    </Scrollbar>
                </div>

                {selectedApt != null && <AptInfo apt={selectedApt} buildingID={buildingID} buildingAddress={address}/>}
                {selectedApt == null &&
                    <div className="second-container empty-table">{t('reports.debtListNew.selectApartment')}</div>}

            </div>

            <div id="section-to-print" ref={pdfContainer}>
                {pdfContainerActive && (
                    <StyledPdfWrapper ltr={ltr}>
                        <div className='pdf-title'>{t('reports.debt_report_title')} <span>{address}</span></div>

                        <div className='pdf-rows-wrapper'>
                            <div className='pdf-row'>
                                <div className='pdf-highlited debt'>{t('reports.apt')}</div>
                                <div className='pdf-highlited debt'>{t('reports.debt_type')}</div>
                                <div className='pdf-highlited debt'>{t('expenses.description')}</div>
                                <div className='pdf-highlited debt'>{t('reports.amount')}</div>
                            </div>

                            {filteredApartments.map((apt, i) => renderPdfRows(apt, i))}

                            <div className='pdf-row'>
                                <div className='debt'></div>
                                <div className='pdf-highlited debt'>{t('reports.total')}</div>
                                <div className='debt'></div>
                                <div className='pdf-highlited debt'>{totalDebt}</div>
                            </div>
                        </div>

                        <div className='pdf-note'>{t('reports.pdf_note')}</div>

                        <div className='pdf-title'>{t('reports.tenant_list_title')} <span>{address}</span></div>

                        <div className='pdf-rows-wrapper'>
                            <div className='pdf-row'>
                                <div className='yearly-apt'>{t('reports.apt')}</div>
                                <div className='yearly-apt'>{t('payments.renter')}</div>
                                <div className='yearly-apt'>{t('reports.tenantPhone')}</div>
                                <div className='yearly-apt'>{t('reports.tenantEmail')}</div>
                                <div className='yearly-apt'>{t('payments.owner')}</div>
                                <div className='yearly-apt'>{t('reports.tenantPhone')}</div>
                                <div className='yearly-apt'>{t('reports.tenantEmail')}</div>
                                <div className='yearly-apt'>{t('reports.monthlyFee')}</div>
                            </div>

                            {filteredApartments.map((apt, i) => renderTenantRows(apt, i))}
                        </div>
                    </StyledPdfWrapper>
                )}
            </div>

            {showSendToAllModal && (
                <SendToAllModal
                    onSendAllModalHide={() => setShowSendToAllModal(false)}
                    ltr={ltr}
                    title={title}
                    buildingID={buildingID}
                    apartmentTenantList={apartmentTenantList}
                    buildingAddress={address}
                />
            )}

            {showFilteringModal && (
                <FilteringModal
                    ltr={ltr}
                    buildingTitle={title}
                    filters={filters}
                    onHide={() => setShowFilteringModal(false)}
                    onConfirm={filters => handleFiltersChange(filters)}
                />
            )}
        </BorderedPageWrapperStyled>
    )
}

export default DebtListNew;
